import { QueryClient } from '@tanstack/react-query';

/** Aligns with origin Cache-Control max-age for catalog GETs. */
export const QUERY_STALE_TIME_MS = 60_000;

/** Aligns with ~7 min catalog TTL (v4 uses cacheTime, not gcTime). */
export const QUERY_CACHE_TIME_MS = 420_000;

/**
 * Customer catalog path fragments (match HttpCache CustomerAllowPaths).
 * Rare on AdminWeb BaseUrl (/api/Web/), but kept for consistency if a GET proxies catalog.
 */
const SHARED_CATALOG_PATH_FRAGMENTS = [
  'Customer/ProductItem/',
  'Customer/ProductItemCategory/',
  'Customer/ProductItemSubCategory/',
  'Customer/ProductItemSubCategory2/',
  'Customer/Brand/',
  'Customer/City/',
  'Customer/State/',
  'Customer/SponsorBanner/',
  'Customer/AppShowcase/',
] as const;

const AUTH_KEY_FIELDS = new Set([
  'AccessToken',
  'DeviceID',
  'DeviceId',
  'AppVersion',
]);

type JsonLike =
  | string
  | number
  | boolean
  | null
  | JsonLike[]
  | { [key: string]: JsonLike };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
      cacheTime: QUERY_CACHE_TIME_MS,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Expose for Playwright e2e assertions (in-memory QueryClient singleton).
if (typeof window !== 'undefined') {
  (window as Window & { __HP_QUERY_CLIENT__?: QueryClient }).__HP_QUERY_CLIENT__ = queryClient;
}

export function isSharedCatalogUrl(url: string): boolean {
  const value = (url || '').toLowerCase();
  return SHARED_CATALOG_PATH_FRAGMENTS.some(fragment =>
    value.includes(fragment.toLowerCase()),
  );
}

function normalizeJsonLike(value: unknown): JsonLike {
  if (value === null) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.map(v => normalizeJsonLike(v));
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, normalizeJsonLike(v)] as const);
    return Object.fromEntries(entries);
  }
  return value as JsonLike;
}

export function stripAuthFields(model: unknown): Record<string, unknown> {
  if (!model || typeof model !== 'object') {
    return {};
  }

  const params: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(model as Record<string, unknown>)) {
    if (!AUTH_KEY_FIELDS.has(key) && value !== undefined) {
      params[key] = value;
    }
  }
  return params;
}

export function normalizeQueryParams(model: unknown): Record<string, JsonLike> {
  const stripped = stripAuthFields(model);
  const entries = Object.entries(stripped)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => [k, normalizeJsonLike(v)] as const);
  return Object.fromEntries(entries);
}

function identityFragment(model: unknown): string {
  if (!model || typeof model !== 'object') {
    return '';
  }
  return String((model as Record<string, unknown>).AccessToken || '');
}

/** Stable Query key for BaseApi GETs. Catalog URLs omit auth; other GETs include identity. */
export function buildApiQueryKey(url: string, model: unknown): unknown[] {
  const params = normalizeQueryParams(model);
  if (isSharedCatalogUrl(url)) {
    return ['api', 'shared', url, params];
  }
  return ['api', 'private', url, params, identityFragment(model)];
}

export function invalidateApiQueriesByPrefix(pathPrefix: string): void {
  void queryClient.invalidateQueries({
    queryKey: ['api'],
    predicate: query => {
      const key = query.queryKey as unknown[];
      const url = String(key?.[2] || '');
      return url.startsWith(pathPrefix);
    },
  });
}

export function invalidateApiQueries(): void {
  void queryClient.invalidateQueries({ queryKey: ['api'] });
}

export function isApiFailureResponse(error: unknown): boolean {
  if (error === null || typeof error !== 'object') {
    return false;
  }
  return (error as { IsSuccess?: boolean }).IsSuccess === false;
}
